import { useState } from "react";
import axios from "axios";
import { 
  FileText, 
  Search, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LogAnalyzer() {
  const [logs, setLogs] = useState("");
  const [osType, setOsType] = useState("linux");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!logs.trim()) {
      toast.error("Please paste some logs to analyze");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze-logs`, {
        logs: logs,
        os_type: osType
      });
      setAnalysis(response.data.analysis);
      toast.success("Analysis complete");
    } catch (error) {
      toast.error("Failed to analyze logs");
    } finally {
      setLoading(false);
    }
  };

  const sampleLogs = {
    linux: `Jan 15 10:23:45 server1 sshd[12345]: Failed password for root from 192.168.1.100 port 22 ssh2
Jan 15 10:23:46 server1 sshd[12345]: Failed password for root from 192.168.1.100 port 22 ssh2
Jan 15 10:23:47 server1 sshd[12345]: Failed password for root from 192.168.1.100 port 22 ssh2
Jan 15 10:24:01 server1 kernel: [UFW BLOCK] IN=eth0 OUT= MAC=... SRC=10.0.0.5 DST=192.168.1.1 PROTO=TCP
Jan 15 10:25:12 server1 systemd[1]: nginx.service: Main process exited, code=exited, status=1/FAILURE
Jan 15 10:25:12 server1 systemd[1]: nginx.service: Failed with result 'exit-code'.
Jan 15 10:30:00 server1 CRON[54321]: (root) CMD (/usr/local/bin/backup.sh)
Jan 15 10:30:05 server1 backup.sh: ERROR: Disk space critical - only 5% remaining`,
    windows: `Error	1/15/2024 10:23:45 AM	Service Control Manager	7034	None	The Print Spooler service terminated unexpectedly.
Warning	1/15/2024 10:24:01 AM	DCOM	10016	None	The application-specific permission settings do not grant Local Activation permission
Error	1/15/2024 10:25:12 AM	Application Error	1000	None	Faulting application name: explorer.exe
Information	1/15/2024 10:30:00 AM	Security-Auditing	4625	None	An account failed to log on.
Error	1/15/2024 10:35:22 AM	Disk	11	None	The driver detected a controller error on \\Device\\Harddisk1\\DR1`
  };

  return (
    <div className="p-6 space-y-6" data-testid="log-analyzer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Log Analyzer</h1>
          <p className="text-zinc-400 text-sm mt-1">AI-powered log analysis and troubleshooting</p>
        </div>
        <Select value={osType} onValueChange={setOsType}>
          <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white" data-testid="log-os-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            <SelectItem value="linux">Linux</SelectItem>
            <SelectItem value="windows">Windows</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Paste Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={logs}
              onChange={(e) => setLogs(e.target.value)}
              placeholder="Paste your server logs here..."
              className="min-h-[300px] bg-black/50 border-white/10 text-white font-mono text-sm"
              data-testid="logs-input"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleAnalyze}
                disabled={loading || !logs.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white glow-blue"
                data-testid="analyze-logs-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Analyze Logs
              </Button>
              <Button
                variant="outline"
                onClick={() => setLogs(sampleLogs[osType])}
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                data-testid="load-sample-btn"
              >
                Load Sample
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Analysis Section */}
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-green-400" />
              Analysis Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!analysis ? (
              <div className="h-[300px] flex flex-col items-center justify-center text-center">
                <FileText className="w-12 h-12 text-zinc-600 mb-4" />
                <p className="text-zinc-400">Paste logs and click Analyze to get AI insights</p>
              </div>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none max-h-[400px] overflow-y-auto" data-testid="analysis-results">
                <ReactMarkdown
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      if (inline) {
                        return <code className="bg-black/50 px-1.5 py-0.5 rounded text-blue-300 font-mono text-sm" {...props}>{children}</code>;
                      }
                      return (
                        <pre className="bg-black/60 border border-white/10 rounded-lg p-4 overflow-x-auto my-2">
                          <code className="font-mono text-sm text-green-300" {...props}>{children}</code>
                        </pre>
                      );
                    },
                    pre: ({ children }) => <>{children}</>,
                    p: ({ children }) => <div className="mb-3 text-zinc-300">{children}</div>,
                    h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-white">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-lg font-semibold mb-2 text-white border-b border-white/10 pb-1">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-base font-medium mb-2 text-white">{children}</h3>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="text-zinc-300">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                  }}
                >
                  {analysis}
                </ReactMarkdown>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Reference */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-white">Log Severity Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5">
              <Info className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-sm font-medium text-white">Info</p>
                <p className="text-xs text-zinc-500">Normal operations</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-sm font-medium text-white">Warning</p>
                <p className="text-xs text-zinc-500">Potential issues</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5">
              <XCircle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-sm font-medium text-white">Error</p>
                <p className="text-xs text-zinc-500">Failures occurred</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-sm font-medium text-white">Success</p>
                <p className="text-xs text-zinc-500">Completed tasks</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
